/**
 * Which exercise a "Change Exercise" pick from the Training page applies to.
 *
 * The page can reload onto another workout while the picker is open, so it
 * remembers which workout and which exercise the picker was opened for, and this
 * decides on return whether that exercise is still on screen. It lives outside the
 * page so the plain-Node test runner can load it. Why (#4634):
 * `design-docs/exercise-replacement.md`, "The Training page's Change Exercise".
 */

/** What the page remembers when it opens the picker. */
export interface TrainingSwitchCapture {
  /** the index of the exercise in the displayed list when the picker opened */
  exerciseIndex: number;
  /** the `_id` of the workout log entry the page was showing */
  logWorkoutId: string;
  /** the plan sub-slot of the exercise the picker was opened for */
  subSlotId: string;
}

/** The part of a displayed exercise this decision reads. */
export interface SwitchTargetCandidate {
  logExercise: { subSlotId: string };
}

export enum TrainingSwitchTargetKind {
  /** the exercise the picker was opened for is still on screen */
  Found = 'Found',
  /** the page is now showing a different workout */
  StaleWorkout = 'StaleWorkout',
  /** the same workout is shown, but the exercise is no longer in it */
  StaleExercise = 'StaleExercise',
}

export type TrainingSwitchTarget<T> =
  | { kind: TrainingSwitchTargetKind.Found; exercise: T }
  | { kind: TrainingSwitchTargetKind.StaleWorkout }
  | { kind: TrainingSwitchTargetKind.StaleExercise };

/**
 * Find the exercise a pick applies to, or say why there is none.
 *
 * if the displayed workout changed, nothing on screen is the target. otherwise
 * the exercise at the captured index wins when it still has the captured
 * sub-slot (the ordinary case, and it keeps two entries sharing a sub-slot
 * apart); failing that, the first exercise with that sub-slot.
 */
export function resolveTrainingSwitchTarget<T extends SwitchTargetCandidate>(
  capture: TrainingSwitchCapture,
  currentLogWorkoutId: string,
  exercises: readonly T[],
): TrainingSwitchTarget<T> {
  if (!capture.logWorkoutId || capture.logWorkoutId !== currentLogWorkoutId) {
    return { kind: TrainingSwitchTargetKind.StaleWorkout };
  }

  const atIndex = exercises[capture.exerciseIndex];
  if (atIndex && atIndex.logExercise?.subSlotId === capture.subSlotId) {
    return { kind: TrainingSwitchTargetKind.Found, exercise: atIndex };
  }

  const bySubSlot = exercises.find(e => e.logExercise?.subSlotId === capture.subSlotId);
  if (bySubSlot) {
    return { kind: TrainingSwitchTargetKind.Found, exercise: bySubSlot };
  }
  return { kind: TrainingSwitchTargetKind.StaleExercise };
}
